import axios, { AxiosError } from "axios";
import { BASE_URL } from "./config";
import { API_ROUTES } from "./endpoints";
import {
  CSRF_HEADER_NAME,
  REFRESH_HEADER_NAME,
  getCsrfToken,
  setCsrfToken,
  clearCsrfToken,
  getRefreshToken,
  setRefreshToken,
  clearRefreshToken,
} from "@/auth/csrfToken";
import { notifyTokenRefreshed } from "@/auth/sessionSync";
import { currentActingChildId } from "@/modules/parentPortal/selectedChild";
import { applyFriendlyErrorMessage } from "./friendlyError";

// withCredentials: true is required so the HttpOnly refresh-token cookie
// (set by /auth/login) is automatically sent on /auth/refresh calls.
export const privateClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

// A higher-level admin previewing a tenant's panel (?viewInstitutionId=… etc., see routes/viewAs.ts) can look but not change anything.
const PREVIEW_PARAMS = ["viewOrganizationId", "viewCampusId", "viewInstitutionId"];
const isPreviewing = () => {
  const q = new URLSearchParams(window.location.search);
  return PREVIEW_PARAMS.some((p) => q.has(p));
};

// While previewing, requests carry a short-lived read-only token from POST /auth/preview-token instead of the real access token, so the
// server itself refuses any write (see requireAuth). It lives in memory only — each preview tab fetches its own — and is renewed before it expires.
let previewToken: { token: string; exp: number; mainToken: string } | null = null;
let previewTokenPending: Promise<string | null> | null = null;

function tokenExpiry(token: string): number {
  try {
    return JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))).exp as number;
  } catch {
    return 0;
  }
}

async function getPreviewToken(): Promise<string | null> {
  const mainToken = localStorage.getItem("token");
  if (!mainToken) return null;
  if (previewToken && previewToken.mainToken === mainToken && previewToken.exp - Date.now() / 1000 > 120) return previewToken.token;
  if (!previewTokenPending) {
    previewTokenPending = axios
      .post(`${BASE_URL}${API_ROUTES.AUTH_PREVIEW_TOKEN}`, undefined, { headers: { Authorization: `Bearer ${mainToken}` }, withCredentials: true })
      .then(({ data }) => {
        previewToken = { token: data.token, exp: tokenExpiry(data.token), mainToken };
        return data.token as string;
      })
      .catch(() => null)
      .finally(() => { previewTokenPending = null; });
  }
  return previewTokenPending;
}

privateClient.interceptors.request.use(async (config) => {
  const method = (config.method ?? "get").toLowerCase();
  if (method !== "get" && method !== "head" && method !== "options" && !String(config.url ?? "").includes("/auth/") && isPreviewing()) {
    const message = "This is a read-only preview. Open the account's own panel to make changes.";
    return Promise.reject(new AxiosError(message, "ERR_PREVIEW_READ_ONLY", config, undefined, { status: 403, statusText: "Forbidden", data: { error: message }, headers: {}, config } as never));
  }
  const previewing = isPreviewing();
  if (previewing) config.headers["X-Preview-Mode"] = "1";
  const token = (previewing ? await getPreviewToken() : null) ?? localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // A parent account acts as one linked child; the server verifies the link on every request.
  const childId = currentActingChildId();
  if (childId) config.headers["X-Student-Id"] = String(childId);
  return config;
});

// --- Silent refresh-and-retry ------------------------------------------
//
// When any request comes back 401 (access token expired), we:
//   1. Call POST /auth/refresh (no body needed — the refresh token travels
//      as an HttpOnly cookie automatically, thanks to withCredentials).
//   2. Store the new access token and retry the original request with it.
//   3. If multiple requests 401 at the same moment, only the first one
//      triggers a refresh; the rest queue and wait for that single
//      refresh to finish, then retry with the new token.
//   4. If the refresh itself fails (refresh token expired/revoked too),
//      clear local session and redirect to login, same as before.

let isRefreshing = false;
let refreshSubscribers: Array<(token: string) => void> = [];
let refreshFailureSubscribers: Array<(err: unknown) => void> = [];

function subscribeTokenRefresh(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

function subscribeTokenRefreshFailure(cb: (err: unknown) => void) {
  refreshFailureSubscribers.push(cb);
}

function onRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
  refreshFailureSubscribers = [];
}

function onRefreshFailed(err: unknown) {
  refreshFailureSubscribers.forEach((cb) => cb(err));
  refreshSubscribers = [];
  refreshFailureSubscribers = [];
}

function redirectToLogin(reason: "session_expired" | "tenant_inactive" = "session_expired") {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  clearCsrfToken();
  clearRefreshToken();
  if (!window.location.pathname.includes("/login")) {
    window.location.href = `/login?reason=${reason}`;
  }
}

/**
 * Runs `fn` under a lock shared across every same-origin tab (via the Web
 * Locks API where available). Refresh tokens are single-use/rotating and
 * live in localStorage (shared by all tabs) — without this, two tabs whose
 * access tokens expire at nearly the same instant (e.g. both opened at
 * login, both scheduled by SessionKeepAlive off the same exp) each read the
 * same not-yet-rotated refresh token and race POST /auth/refresh. The loser
 * presents an already-revoked token, 401s, and redirectToLogin() clears
 * localStorage — killing the *winner's* freshly-saved session too. Falls
 * back to running fn directly if Web Locks isn't supported.
 */
function withCrossTabRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  if (typeof navigator !== "undefined" && "locks" in navigator) {
    return (navigator as any).locks.request("edumanageplus-auth-refresh", fn);
  }
  return fn();
}

async function doRefreshAccessToken(): Promise<string> {
  const { data } = await privateClient.post(API_ROUTES.AUTH_REFRESH, undefined, {
    headers: {
      [CSRF_HEADER_NAME]: getCsrfToken() ?? "",
      // The refresh token is sent explicitly, not as an ambient cookie —
      // see REFRESH_HEADER_NAME in auth/csrfToken.ts for why.
      [REFRESH_HEADER_NAME]: getRefreshToken() ?? "",
    },
  });
  const newToken = data.token;
  localStorage.setItem("token", newToken);
  if (data.csrfToken) setCsrfToken(data.csrfToken);
  // Backend rotates the refresh token on every refresh — persist the new one
  // or the next silent refresh will present a revoked token and 401.
  if (data.refreshToken) setRefreshToken(data.refreshToken);
  notifyTokenRefreshed(newToken);
  return newToken;
}

/**
 * Calls POST /auth/refresh and stores the renewed access token in both
 * localStorage and Redux (via sessionSync.ts). Shared by the reactive
 * 401-interceptor below and by SessionKeepAlive's proactive renewal, so
 * there's one code path for "we obtained a new access token". Cross-tab
 * safe — see withCrossTabRefreshLock above.
 */
export async function refreshAccessToken(): Promise<string> {
  const refreshTokenBeforeLock = getRefreshToken();
  return withCrossTabRefreshLock(async () => {
    // A sibling tab may have already refreshed while we waited for the
    // lock — if the stored refresh token moved on, a new access token is
    // already in localStorage too; reuse it instead of rotating again.
    if (getRefreshToken() !== refreshTokenBeforeLock) {
      const existingToken = localStorage.getItem("token");
      if (existingToken) return existingToken;
    }
    return doRefreshAccessToken();
  });
}

privateClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // The institution this login belongs to was deactivated or deleted: there is nothing to refresh, so sign out straight away.
    if (error.response?.status === 401 && error.response?.data?.code === "TENANT_INACTIVE") {
      redirectToLogin("tenant_inactive");
      return applyFriendlyErrorMessage(error);
    }

    if (error.response?.status !== 401 || originalRequest._retry) {
      return applyFriendlyErrorMessage(error);
    }

    if (
      originalRequest.url?.includes(API_ROUTES.AUTH_REFRESH) ||
      originalRequest.url?.includes(API_ROUTES.AUTH_LOGIN)
    ) {
      redirectToLogin();
      return applyFriendlyErrorMessage(error);
    }

    originalRequest._retry = true;

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh((newToken: string) => {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          resolve(privateClient(originalRequest));
        });
        subscribeTokenRefreshFailure((refreshError) => {
          reject(refreshError);
        });
        // Safety-net only: onRefreshed/onRefreshFailed should always settle
        // this promise first. Guards against a refresh call that never
        // resolves or rejects (e.g. hung network request).
        setTimeout(() => {
          if (refreshSubscribers.length || refreshFailureSubscribers.length) {
            refreshSubscribers = [];
            refreshFailureSubscribers = [];
            reject(error);
          }
        }, 10000);
      });
    }

    isRefreshing = true;

    try {
      // /auth/refresh is CSRF-protected (double-submit token — see
      // backend/src/middleware/csrf.js) since it authenticates purely off
      // the ambient refreshToken cookie with no Authorization header.
      const newToken = await refreshAccessToken();
      isRefreshing = false;
      onRefreshed(newToken);

      originalRequest.headers.Authorization = `Bearer ${newToken}`;
      return privateClient(originalRequest);
    } catch (refreshError) {
      isRefreshing = false;
      onRefreshFailed(refreshError);
      const refreshStatus = (refreshError as { response?: { status?: number } })?.response?.status;
      if (refreshStatus === 401 || refreshStatus === 403) redirectToLogin();
      return applyFriendlyErrorMessage(refreshError as Parameters<typeof applyFriendlyErrorMessage>[0]);
    }
  }
);